import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, withCtx as _withCtx, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "cta-button-wrapper" }
const _hoisted_2 = ["href", "target"]
const _hoisted_3 = {
  key: 2,
  class: "text-danger",
  style: {"margin-top":"2rem"}
}

import { computed, ref, watchEffect } from "vue";
import axios, { AxiosError } from "axios";
import { useRoute } from "vue-router";
import cms from "../../../services/cms/cms";
import SessionSlide from "../../sessions/session-slide.vue";
import VisualPositioningCanvas from "../../sessions/visual-positioning-canvas.vue";
import SessionCompleteButton, { CmsSessionDoc } from "../session-complete-button.vue";
import CmsImg from "../cms-img.vue";
import {
  CmsLink,
  CmsSessionSlideWithButton,
  SessionType,
} from "../../../services/cms/cms-type-definitions";
import { PrismicSliceWithData } from "../../../views/cms/cms-session.vue";
import delay from "../../../services/utilities/delay";
import { useCmsSessionStore } from "../../../stores/useCmsSessionStore";
import pinia from "../../../stores";
import SlideText from "./slide-text.vue";
import { useAxios } from "../../../composables/api";
import { ResultApiModel } from "../../../api-models";
import cmsContentMap from "../../../services/cms/cms-content-map";
import { csContext } from "../../../services/cs-context";
import { useToggle } from "../../../composables/useToggle";


export default /*#__PURE__*/_defineComponent({
  __name: 'text-image-and-button-slide',
  props: {
    slice: {},
    sessionType: {},
    index: {},
    hasCompleteButton: { type: Boolean },
    doc: {},
    closeUrl: {},
    levelNumber: {}
  },
  setup(__props: any, { expose: __expose }) {

const props = __props;

const cmsSessionStore = useCmsSessionStore(pinia());

const completed = ref(false);
const errorMessage = ref<string>();

const prismicSlice = computed<CmsSessionSlideWithButton>(() => {
  return props.slice.prismicSlice as CmsSessionSlideWithButton;
});

const { state: isDisplayed, toggleState: toggleIsDisplayed } = useToggle(false);

const image = computed(() => prismicSlice.value.primary.image);
const imageExists = computed(() => !!image.value?.url);
const buttonText = computed(() => cms.asText(prismicSlice.value.primary.button_text));
const isJyreInsightsSolo = computed<boolean>(() => {
  return props.doc?.uid === cmsContentMap.staticRoutes.gyreInsightsSoloReportUid;
});

const userIsOnAssessPlan = computed<boolean>(() => {
  return csContext.userDocument.platformPermissions.includes("AssessPlanAccess");
});

const buttonLinkTarget = computed(
  () => (prismicSlice.value.primary.button_link as CmsLink)?.target,
);

const isFoundationsPathway = computed<boolean>(() => {
  const foundationPathwayUids = [
    cmsContentMap.staticRoutes.foundationsSessionUid,
    cmsContentMap.staticRoutes.foundationsCustomFrameworkSessionUid,
    cmsContentMap.staticRoutes.foundationsHackingGamesSessionUid,
  ];

  return props.doc?.uid ? foundationPathwayUids.includes(props.doc.uid) : false;
});

const showCloseButton = computed<boolean>(() => {
  return prismicSlice.value.primary.display_close_button ?? false;
});

watchEffect(() => {
  if (!isDisplayed.value && cmsSessionStore.sectionsToDisplay.includes(props.slice.section)) {
    toggleIsDisplayed();
  }
});

// These 3 refs need to be exposed so that cms-session can apply animations to them
const visual = ref<InstanceType<typeof CmsImg> | null>(null);
const slide = ref<InstanceType<typeof SessionSlide> | null>(null);
const visualDiv = computed(() => slide?.value?.visualDiv);

async function postCompleteSession(url: string) {
  const { promise } = useAxios<ResultApiModel>(url, {
    httpMethod: "POST",
    isFatal: false,
  });

  try {
    const response = await promise;
    completed.value = response.success;
  } catch (error) {
    errorMessage.value = "Oops, something went wrong. Please try again.";
  }
}

const route = useRoute();
const levelTitle = computed(() => route.query.levelTitle as string);
const levelId = computed(() => route.query.levelId as string);
const levelUid = computed(() => route.query.levelUid as string);

const buttonLink = computed(() => {
  const link = cms.linkResolver(prismicSlice.value.primary.button_link);

  if (link) {
    return link;
  }

  if (isJyreInsightsSolo.value && userIsOnAssessPlan.value) {
    return "/home";
  }

  // Check to see if user is a team lead and route accoridingly
  if (levelTitle.value?.length > 0) {
    return "/home";
  }

  return props.closeUrl || undefined;
});

const closeLocation = (): string => {
  if (buttonLink.value) {
    return buttonLink.value;
  }

  return "/home";
};

const close = (): void => {
  window.location.href = closeLocation();
};

async function markLevelAsComplete() {
  try {
    await axios.post(`/api/teamlevels/CompleteLevel${csContext.currentTeam?.id}`, {
      title: decodeURIComponent(levelTitle.value),
      prismicId: levelId,
      prismicUid: levelUid,
      teamId: csContext.currentTeam?.id,
      levelNumber: props.levelNumber,
    });
    completed.value = true;
  } catch (error) {
    errorMessage.value = (error as AxiosError).response?.data.message;
  }
}

const complete = async (): Promise<void> => {
  const delayPromise = delay(3000);
  if (isFoundationsPathway.value) {
    await postCompleteSession("/api/foundation/complete");
  } else if (props.sessionType === "level") {
    await markLevelAsComplete();
  } else {
    completed.value = true;
  }

  await delayPromise;
  if (!errorMessage.value) {
    window.location.href = closeLocation();
  }
};

__expose({
  visualDiv,
  visual,
  slide,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(SessionSlide, {
    ref_key: "slide",
    ref: slide,
    id: 'slide' + props.index,
    "visual-position": "fixed",
    class: _normalizeClass({
      'slide-after-activity': !_unref(isDisplayed),
      'submitted-mapping': _unref(isDisplayed),
    }),
    "data-test": `session-slide-${_ctx.index}`
  }, {
    text: _withCtx(() => [
      _createVNode(SlideText, {
        text: prismicSlice.value.primary.text_body
      }, null, 8, ["text"]),
      _createElementVNode("div", _hoisted_1, [
        (!!props.hasCompleteButton)
          ? (_openBlock(), _createBlock(SessionCompleteButton, {
              key: 0,
              buttonProps: {
            completeButtonText: buttonText.value,
            completed: completed.value,
            isForPathwayStep: false,
            buttonLink: buttonLink.value,
          },
              "show-close-button": showCloseButton.value,
              onClose: close,
              onComplete: complete
            }, null, 8, ["buttonProps", "show-close-button"]))
          : (_openBlock(), _createElementBlock("a", {
              key: 1,
              class: "btn btn-lg btn-primary journey-next-step",
              href: buttonLink.value,
              target: buttonLinkTarget.value
            }, _toDisplayString(buttonText.value), 9, _hoisted_2)),
        (errorMessage.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_3, _toDisplayString(errorMessage.value), 1))
          : _createCommentVNode("", true)
      ])
    ]),
    visual: _withCtx(() => [
      _createVNode(VisualPositioningCanvas, null, {
        default: _withCtx(() => [
          (imageExists.value)
            ? (_openBlock(), _createBlock(CmsImg, {
                key: 0,
                ref_key: "visual",
                ref: visual,
                class: "slide-image",
                src: image.value
              }, null, 8, ["src"]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["id", "class", "data-test"]))
}
}

})